import type { ApiErrorCode, ApiErrorResponse } from '@oscom/shared';

/**
 * Typed API client.
 *
 * The response types come from `@oscom/shared`, so a backend change that breaks
 * the contract breaks the frontend build instead of production.
 *
 * The client never treats a 401/403 as an exception to be swallowed: it maps
 * them to a typed error so the UI can react (login again / access denied).
 */

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiErrorCode | 'unknown',
    message: string,
    readonly correlationId?: string,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }

  get isUnauthenticated(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

export function isApiErrorResponse(value: unknown): value is ApiErrorResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const candidate = (value as ApiErrorResponse).error;
  return (
    typeof candidate === 'object' &&
    candidate !== null &&
    typeof candidate.code === 'string' &&
    typeof candidate.message === 'string'
  );
}

export async function apiGet<TResponse>(route: string): Promise<TResponse> {
  let response: Response;
  try {
    response = await fetch(route, {
      method: 'GET',
      headers: { accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
  } catch {
    throw new ApiRequestError(0, 'unknown', 'Der Dienst ist nicht erreichbar.');
  }

  const rawBody: unknown = await response
    .clone()
    .json()
    .catch(() => undefined);

  if (!response.ok) {
    if (isApiErrorResponse(rawBody)) {
      throw new ApiRequestError(
        response.status,
        rawBody.error.code,
        rawBody.error.message,
        rawBody.error.correlationId,
      );
    }
    throw new ApiRequestError(
      response.status,
      'unknown',
      `Die Anfrage ist fehlgeschlagen (HTTP ${response.status}).`,
    );
  }

  if (rawBody === undefined) {
    throw new ApiRequestError(response.status, 'unknown', 'Unerwartete Antwort des Dienstes.');
  }
  return rawBody as TResponse;
}
